/**
 * Procedural vegetation atlas (issue #44): one small canvas texture shared by
 * every tree/shrub/ground-cover instanced card — never one texture per plant
 * (issue #17). Mirrors drystoneTexture.ts / facadeTexture.ts: pure three-free
 * layout maths unit-tested in Node; canvas painting is a thin wrapper
 * exercised in e2e (happy-dom has no 2D context, so the painter must no-op to
 * an unpainted/transparent canvas rather than crash under test — the same
 * contract makeDrystoneTexture() uses).
 *
 * Purely cosmetic scene furniture — NO bearing on any LOS result (hard
 * constraint #1, guarded by vegetation.guard.test.ts).
 *
 * Layout: a small ATLAS_COLS x ATLAS_ROWS grid, one cell per (kind, variant)
 * pair. Cards sample only their own cell (alpha-tested cutout: transparent
 * background, opaque plant silhouette), so vegetation.ts can bucket instances
 * by (kind, variant) into a handful of InstancedMesh objects that all bind
 * this ONE shared texture.
 *
 * Canopy silhouette shape (issue #57 repaint — tree/shrub cells used to be one
 * flat-filled ellipse/triangle, reading as a "black lollipop" at any distance)
 * is adapted from Braffolk/fable5-world-demo (MIT licence, commit fd75fdb):
 * a jittered rosette of foliage lobes with visible sky-gaps between them,
 * plus a couple of darker branch wedges showing through those gaps, instead
 * of one solid fill shape.
 */
import { CanvasTexture, ClampToEdgeWrapping, SRGBColorSpace } from 'three';
import type { VegetationKind } from './vegetationPlacement';
import { GROUNDCOVER_VARIANTS, SHRUB_VARIANTS, TREE_VARIANTS } from './vegetationPlacement';

export const ATLAS_COLS = 4;
export const ATLAS_ROWS = 2;
/** Atlas cell size (px). Raised 128 -> 512 (issue #57) so the repainted
 * lobed canopy structure has enough resolution to read as clustered lobes
 * with gaps rather than a soft blob. */
export const CELL_PX = 512;

/** Deterministic fractional hash in [0, 1) (same idiom as roadWalls.ts). */
function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** Fixed cell order within the atlas grid — index 0 is column 0/row 0, and
 * indices advance left-to-right then top-to-bottom of CELL_ORDER (mapped to
 * bottom-to-top texture rows in atlasCellUvRect, since canvas y grows down
 * but UV v grows up). */
const CELL_ORDER: ReadonlyArray<{ kind: VegetationKind; variant: number }> = (() => {
  const order: Array<{ kind: VegetationKind; variant: number }> = [];
  for (let v = 0; v < TREE_VARIANTS; v++) order.push({ kind: 'tree', variant: v });
  for (let v = 0; v < SHRUB_VARIANTS; v++) order.push({ kind: 'shrub', variant: v });
  for (let v = 0; v < GROUNDCOVER_VARIANTS; v++) order.push({ kind: 'groundcover', variant: v });
  return order;
})();

if (CELL_ORDER.length > ATLAS_COLS * ATLAS_ROWS) {
  // Build-time assertion: the palette must fit the atlas grid (never
  // silently alias two species onto the same cell).
  throw new Error(
    `vegetation atlas has ${CELL_ORDER.length} cells but only ${ATLAS_COLS * ATLAS_ROWS} grid slots are available`,
  );
}

export interface AtlasUvRect {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

/** UV rect (three.js convention: v=0 at bottom) for one (kind, variant) cell.
 * Throws for an unknown kind/variant rather than silently reusing another
 * cell's texture. */
export function atlasCellUvRect(kind: VegetationKind, variant: number): AtlasUvRect {
  const index = CELL_ORDER.findIndex((c) => c.kind === kind && c.variant === variant);
  if (index < 0) throw new Error(`no vegetation atlas cell for ${kind}:${variant}`);
  const col = index % ATLAS_COLS;
  const row = Math.floor(index / ATLAS_COLS);
  const u0 = col / ATLAS_COLS;
  const u1 = (col + 1) / ATLAS_COLS;
  // row 0 (first defined) is placed at the TOP of the canvas; canvas y grows
  // down while UV v grows up, so row 0 maps to the highest v band.
  const v1 = 1 - row / ATLAS_ROWS;
  const v0 = 1 - (row + 1) / ATLAS_ROWS;
  return { u0, v0, u1, v1 };
}

let cached: CanvasTexture | undefined;

/** Silhouette fill colour: green hue jitter per lobe/cell so the small
 * palette still reads as mixed species, not one repeated cutout. Lightness
 * raised and widened (was 24-40%, read as near-black even before the card
 * normal fix) so foliage has visible light/dark variation across lobes
 * (issue #57). */
function foliageFill(seed: number): string {
  const hue = 82 + hash01(seed * 3.1) * 46; // 82-128: yellow-green through to a cooler green
  const sat = 34 + hash01(seed * 5.7) * 30; // 34-64%
  const light = 34 + hash01(seed * 7.3) * 26; // 34-60%: lighter and more varied
  return `hsl(${hue.toFixed(0)}, ${sat.toFixed(0)}%, ${light.toFixed(0)}%)`;
}

/** A single foliage lobe in 0..1 cell-fraction space (canvas convention: y
 * grows down). `fillSeed` drives that lobe's own colour draw so a crown reads
 * as several differently-toned clumps rather than one flat fill. */
export interface FoliageLobe {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  fillSeed: number;
}

/** A thin branch silhouette (3 points, 0..1 cell-fraction space) drawn under
 * the foliage lobes so it shows through the sky-gaps between them. */
export interface BranchWedge {
  points: [[number, number], [number, number], [number, number]];
}

/**
 * Deterministic rosette of `count` foliage lobes around (0.5, centerY):
 * angularly spread with per-lobe jitter, each lobe's own radius capped at a
 * fraction of the rosette radius. Capping the lobe radius below the nominal
 * spacing between neighbours means the rosette reads as a cluster of
 * separate lobes with visible gaps between/around them, never a single solid
 * disc — the core shape fix for issue #57 ("black lollipop" -> lobed
 * silhouette). Pure and three-free; canvas painting (paintTreeCell /
 * paintShrubCell) just draws whatever this returns.
 */
export function canopyLobes(
  seed: number,
  count: number,
  centerY: number,
  radiusX: number,
  radiusY: number,
): FoliageLobe[] {
  const lobes: FoliageLobe[] = [];
  const angleStep = (Math.PI * 2) / count;
  for (let i = 0; i < count; i++) {
    const jitter = (hash01(seed * 11.3 + i * 2.7) - 0.5) * angleStep * 0.5;
    const angle = angleStep * i + jitter - Math.PI / 2; // first lobe points up
    const distFrac = 0.5 + hash01(seed * 4.9 + i * 3.3) * 0.4; // 0.5-0.9 of rosette radius
    const cx = 0.5 + Math.cos(angle) * radiusX * distFrac;
    const cy = centerY + Math.sin(angle) * radiusY * distFrac;
    const lobeRFrac = 0.24 + hash01(seed * 7.1 + i * 5.1) * 0.1; // 0.24-0.34 of rosette radius
    lobes.push({
      cx,
      cy,
      rx: radiusX * lobeRFrac,
      ry: radiusY * lobeRFrac * (0.85 + hash01(seed * 9.7 + i) * 0.3),
      fillSeed: seed * 97 + i * 13 + 1,
    });
  }
  return lobes;
}

/**
 * Deterministic pair of thin branch wedges fanning up from a trunk point
 * toward the rosette, meant to be drawn UNDER the foliage lobes so they only
 * show where a sky-gap leaves them uncovered — a hint of branch structure
 * rather than a flat green fill (issue #57). Pure and three-free.
 */
export function branchWedges(seed: number, baseY: number, topY: number, spreadX: number): BranchWedge[] {
  const count = 2 + Math.floor(hash01(seed * 6.3) * 2); // 2-3 wedges
  const wedges: BranchWedge[] = [];
  for (let i = 0; i < count; i++) {
    const lean = (hash01(seed * 13.7 + i * 4.1) - 0.5) * spreadX * 1.4;
    const width = spreadX * (0.05 + hash01(seed * 15.9 + i * 2.3) * 0.05);
    const tipX = 0.5 + lean;
    const tipY = topY + (hash01(seed * 17.1 + i) - 0.5) * (baseY - topY) * 0.3;
    wedges.push({
      points: [
        [0.5 - width * 0.4, baseY],
        [0.5 + width * 0.4, baseY],
        [tipX, tipY],
      ],
    });
  }
  return wedges;
}

function drawLobes(ctx: CanvasRenderingContext2D, x0: number, y0: number, w: number, h: number, lobes: FoliageLobe[]): void {
  for (const lobe of lobes) {
    ctx.fillStyle = foliageFill(lobe.fillSeed);
    ctx.beginPath();
    ctx.ellipse(x0 + lobe.cx * w, y0 + lobe.cy * h, lobe.rx * w, lobe.ry * h, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawBranches(ctx: CanvasRenderingContext2D, x0: number, y0: number, w: number, h: number, wedges: BranchWedge[]): void {
  ctx.fillStyle = '#3a2c1e';
  for (const wedge of wedges) {
    ctx.beginPath();
    const [a, b, c] = wedge.points;
    ctx.moveTo(x0 + a[0] * w, y0 + a[1] * h);
    ctx.lineTo(x0 + b[0] * w, y0 + b[1] * h);
    ctx.lineTo(x0 + c[0] * w, y0 + c[1] * h);
    ctx.closePath();
    ctx.fill();
  }
}

function paintTreeCell(ctx: CanvasRenderingContext2D, x0: number, y0: number, seed: number, variant: number): void {
  const w = CELL_PX;
  const h = CELL_PX;
  ctx.fillStyle = '#4a3626';
  ctx.fillRect(x0 + w * 0.47, y0 + h * 0.62, w * 0.06, h * 0.36); // trunk stub

  if (variant === 1) {
    // conical conifer: three stacked, narrowing rosettes read as tiered branches.
    const tiers: Array<[number, number, number]> = [
      [0.28, 0.42, 0.16],
      [0.4, 0.32, 0.13],
      [0.52, 0.22, 0.1],
    ];
    for (const [centerY, radiusX, radiusY] of tiers) {
      drawBranches(ctx, x0, y0, w, h, branchWedges(seed * 31 + centerY * 1000, centerY + radiusY * 0.6, centerY - radiusY, radiusX));
      drawLobes(ctx, x0, y0, w, h, canopyLobes(seed + centerY * 1000, 5, centerY, radiusX, radiusY));
    }
  } else if (variant === 2) {
    // small sapling: one tight low cluster.
    drawBranches(ctx, x0, y0, w, h, branchWedges(seed, 0.62, 0.3, 0.22));
    drawLobes(ctx, x0, y0, w, h, canopyLobes(seed, 5, 0.42, 0.26, 0.24));
  } else {
    // round deciduous crown (default / variant 0).
    drawBranches(ctx, x0, y0, w, h, branchWedges(seed, 0.6, 0.16, 0.3));
    drawLobes(ctx, x0, y0, w, h, canopyLobes(seed, 7, 0.32, 0.36, 0.3));
  }
}

function paintShrubCell(ctx: CanvasRenderingContext2D, x0: number, y0: number, seed: number): void {
  const w = CELL_PX;
  const h = CELL_PX;
  drawBranches(ctx, x0, y0, w, h, branchWedges(seed, 0.82, 0.55, 0.28));
  drawLobes(ctx, x0, y0, w, h, canopyLobes(seed, 5, 0.68, 0.34, 0.22));
}

function paintGroundcoverCell(ctx: CanvasRenderingContext2D, x0: number, y0: number, seed: number): void {
  const w = CELL_PX;
  const h = CELL_PX;
  ctx.strokeStyle = foliageFill(seed);
  ctx.lineWidth = Math.max(2, w * 0.03);
  for (let i = 0; i < 9; i++) {
    const bx = x0 + w * (0.15 + hash01(seed * 3 + i * 1.7) * 0.7);
    const by = y0 + h * 0.92;
    const lean = (hash01(seed * 5 + i) - 0.5) * w * 0.14;
    const bladeLen = h * (0.28 + hash01(seed * 7 + i) * 0.18);
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx + lean, by - bladeLen);
    ctx.stroke();
  }
}

/**
 * Builds (or returns the cached) single shared vegetation atlas texture.
 * Callers must reuse this one texture across every instanced card bucket —
 * never build a texture per plant/species.
 */
export function makeVegetationTexture(): CanvasTexture {
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_COLS * CELL_PX;
  canvas.height = ATLAS_ROWS * CELL_PX;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height); // transparent background (alpha-tested cutout)
    for (const [index, cell] of CELL_ORDER.entries()) {
      const col = index % ATLAS_COLS;
      const row = Math.floor(index / ATLAS_COLS);
      const x0 = col * CELL_PX;
      const y0 = row * CELL_PX;
      const seed = index + 1;
      if (cell.kind === 'tree') paintTreeCell(ctx, x0, y0, seed, cell.variant);
      else if (cell.kind === 'shrub') paintShrubCell(ctx, x0, y0, seed);
      else paintGroundcoverCell(ctx, x0, y0, seed);
    }
  }
  const texture = new CanvasTexture(canvas);
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  cached = texture;
  return texture;
}
